import { useDealerFlagTarget } from "@hooks/useDealerFlagTarget";
import { useFeatureFlags } from "@lyteflo/components";
import type { FeatureRequirement } from "@lyteflo/types";
import {
  getFeaturesForRole,
  getRolloutFlag,
  toFeatureList,
  useProfile,
} from "@lyteflo/utils";
import { buildFlagsmithTraitsForDealerConfig } from "@lyteflo/utils/featureFlagsCache";

/**
 * Whether the flags behind `requirement` have been resolved for the dealership
 * currently in context.
 *
 * A route guard that decides before this is true bounces a flagged dealer: the
 * flag set the session starts with is targeted at nobody, and the dealership's
 * own arrives a round trip later.
 */
export const useRolloutFlagsReady = (
  requirement?: FeatureRequirement,
): boolean => {
  const { resolvedTraits } = useFeatureFlags();
  const { profile } = useProfile();
  const { config, isSettled } = useDealerFlagTarget();

  const features = toFeatureList(requirement);
  const roles = profile?.roles ?? [];
  if (!features.some((feature) => getRolloutFlag(feature, roles))) return true;
  // A rollout flag can only widen what a role grants, so a session the role
  // already admits has nothing to wait for.
  const granted = profile ? getFeaturesForRole(profile.roles) : [];
  if (features.some((feature) => granted.includes(feature))) return true;

  // resolvedTraits === null means no flag set has been resolved yet, which is
  // not the same as one resolved for no dealership — only the latter answers
  // for a group or all-dealerships context.
  if (resolvedTraits === null) return false;
  if (!isSettled) return false;
  const requestedTraits = buildFlagsmithTraitsForDealerConfig(config);
  const untargetedTraits = buildFlagsmithTraitsForDealerConfig(null);
  return Object.entries(requestedTraits).every(
    ([key, value]) => (resolvedTraits[key] ?? untargetedTraits[key]) === value,
  );
};
